import { isMeasuredMilking } from "../../utils/milk-recording-checks.js";
import { memo, useState, useCallback, useEffect } from "react";
import { Typography, Box, Button } from "@mui/material";
import {
    checkIfAnimalIsEligible,
    createAnimalMilkRecording,
    getAnimalMilkRecordingsAndAnimals,
    updateAnimalMilkRecording,
    deleteAnimalMilkRecording,
    getAnimalByFlockCode,
    getTablesForAnimalsData,
} from "../../api/index.js";
import Spinner from "../Spinner.js";
import Form from "../Form.js";
import { useSnackbar } from "../../utils/index.js";
import Table from "../Table.js";
import {
    createdAtHeader,
    registrationCodeHeaderNoClick,
    flockCodeHeader,
    birthDateHeaderNotFilterable,
    lambingDateHeaderNotFilterable,
    mostRecentRecordingDateHeaderNotFilterable,
    recordingIncNumberHeaderNotFilterable,
} from "../TableHeaders.js";
import useSelectStyles from "../styles/Select.js";
import AreYouSurePopup from "../popups/AreYouSurePopup.js";
import SelectAnimalPopup from "../popups/SelectAnimalPopup.js";
import Dialog from "../Dialog.js";

const measurementTimeTranslates = [
    { en: "morning", gr: "Πρωινή" },
    { en: "noon", gr: "Μεσημεριανή" },
    { en: "evening", gr: "Βραδινή" },
]

const animalStatusTranslationArray = [
    { en: "Normal", gr: "Κανονική" },
    { en: "Irregular Check", gr: "Μη κανονικός έλεγχος" },
    { en: "Mastitis", gr: "Μαστίτιδα" },
    { en: "Other Disease", gr: "Άλλη αρρώστια" },
];

const AnimalMilkrecordingForm = ({
    selectedMilkRecording,
    isUpdate,
    setIsMilkRecordingSelected,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [selectedAnimal, setSelectedAnimal] = useState();
    const [fetchedAnimalLambing, setFetchedAnimalLambing] = useState();
    const [fetchedAnimals, setFetchedAnimals] = useState([]);
    const [fetchedAnimalsMilkRecordings, setFetchedAnimalsMilkRecordings] = useState([]);
    const [fetchedAnimalsDropdownValues, setFetchedAnimalsDropdownValues] = useState([]);
    const [selectedAnimalMilkRecording, setSelectedAnimalMilkRecording] = useState();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSelectAnimalPopupOpen, setIsAnimalPopupOpen] = useState(false);
    const [animalsForPopup, setAnimalsForPopup] = useState([]);
    const [animalsAfterFortyTwo, setAnimalsAfterFortyTwo] = useState([]);
    const [animalsWaiting, setAnimalsWaiting] = useState([]);
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");

    const { success, error } = useSnackbar();
    const classes = useSelectStyles();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { success: scs, animalMilkRecordings, animals } = await getAnimalMilkRecordingsAndAnimals(selectedMilkRecording._id);
                if (scs) {
                    setFetchedAnimalsMilkRecordings(animalMilkRecordings);
                    setFetchedAnimals(animals);
                    setFetchedAnimalsDropdownValues(
                        animals
                            .map((animal) => ({
                                value: animal?.registrationCode,
                                text: animal?.registrationCode,
                            }))
                            .sort((a, b) => a.value.localeCompare(b.value))
                    );
                }
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )


    const fetchArrayAnimals = async () => {
        setIsLoading(true);
        const { success: scs, animalsAfter42days, animalsWaitingForMilkRecording } = await getTablesForAnimalsData(selectedMilkRecording.owner, selectedMilkRecording._id);
        if (scs) {
            setAnimalsAfterFortyTwo(animalsAfter42days);
            setAnimalsWaiting(animalsWaitingForMilkRecording);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        if (!isUpdate) { fetchArrayAnimals(); }
        if (isUpdate) { fetchData(); }
    }, []);


    useEffect(() => {
        if (step === 1 && !isUpdate) {
            const timeoutId = setTimeout(() => {
                const flockCodeInput = document.getElementById("flockCode");
                if (flockCodeInput) {
                    flockCodeInput.focus();
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [step, isUpdate]);

    const animalFormContent = [
        {
            customType: "input",
            id: "flockCode",
            type: "text",
            placeholder: "4-ψήφιος Κωδικός Ζώου",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επιλογή Ζώου",
        },
    ]

    const handleAnimalSelectFormSubmit = async (values) => {
        setIsLoading(true);
        const { success: scs, animalLambing, animal, message, warning } = await checkIfAnimalIsEligible(selectedMilkRecording._id, values.registrationCode);
        if (scs) {
            setIsAnimalPopupOpen(false);
            setFetchedAnimalLambing(animalLambing);
            setSelectedAnimal(animal);
            if (warning) {
                setWarningMessage(warning);
                setWarningDialogOpen(true);
            } else {
                setStep(2);
            }
        } else {
            error(message)
        }
        setIsLoading(false);
    }

    const animalSubmit = async (values) => {
        setIsLoading(true);

        const { success: scs, animals, message } = await getAnimalByFlockCode(values.flockCode, selectedMilkRecording.owner);
        if (scs) {
            if (animals.length === 1) {
                handleAnimalSelectFormSubmit({ registrationCode: animals[0].registrationCode });
            } else {
                const animalsOptions = animals.map((animal) => ({
                    value: animal?.registrationCode,
                    text: animal?.registrationCode,
                }));
                setAnimalsForPopup(animalsOptions);
                setIsAnimalPopupOpen(true);
            }
        } else {
            error(message);
        }
        setIsLoading(false);
    }



    const animalSelectFormContent = [
        {
            customType: "dropdown",
            id: "animal",
            items: fetchedAnimalsDropdownValues,
            placeholder: "Προβατίνα",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ]

    const handleAnimalDropdownSelectSubmit = async (values) => {
        const selectedAnimalObj = fetchedAnimals.find((animal) => animal?.registrationCode === values.animal);
        setSelectedAnimal(selectedAnimalObj);
        const selectedAnimalMilkRecordingObj = fetchedAnimalsMilkRecordings.find((rcrd) => rcrd.animalId === selectedAnimalObj._id);
        setSelectedAnimalMilkRecording(selectedAnimalMilkRecordingObj);
        setStep(2);
    }

    const selectedAnimalTableColumns = [
        registrationCodeHeaderNoClick,
        createdAtHeader,
        flockCodeHeader,
        birthDateHeaderNotFilterable,
    ]

    const animalsWaitingTableCoumns = [
        registrationCodeHeaderNoClick,
        flockCodeHeader,
        lambingDateHeaderNotFilterable,
        recordingIncNumberHeaderNotFilterable,
        mostRecentRecordingDateHeaderNotFilterable,
    ];

    const animalsAfterFortyTwoTableColumns = [
        registrationCodeHeaderNoClick,
        flockCodeHeader,
        lambingDateHeaderNotFilterable,
    ]

    const animalMilkRecordingFormContent = selectedMilkRecording?.checks
        .filter(isMeasuredMilking)
        .map((check) => ({
            customType: "input",
            id: check.type,
            type: "number",
            placeholder: `${measurementTimeTranslates.find((tr) => tr.en === check.type)?.gr} Μέτρηση Γάλακτος (gr)`,
            required: true,
            value: isUpdate ? selectedAnimalMilkRecording?.checks.find((chk) => chk?.type === check.type)?.milk : null,
        })) || [];

    animalMilkRecordingFormContent.push(
        {
            customType: "dropdown",
            id: "status",
            items: [
                { value: "Κανονική", text: "Κανονική" },
                { value: "Μη κανονικός έλεγχος", text: "Μη κανονικός έλεγχος" },
                { value: "Μαστίτιδα", text: "Μαστίτιδα" },
                { value: "Άλλη αρρώστια", text: "Άλλη αρρώστια" },
            ],
            placeholder: "Κατάσταση Ζώου",
            required: true,
            defaultValue: isUpdate ? animalStatusTranslationArray.find((trn) => trn.en === selectedAnimalMilkRecording?.animalStatus)?.gr : "Κανονική",
        },
    );

    animalMilkRecordingFormContent.push({
        customType: "input",
        id: "sampleNumber",
        type: "text",
        placeholder: "Αριθμός δείγματος",
        value: isUpdate ? selectedAnimalMilkRecording?.sampleNumber : null,
    });

    animalMilkRecordingFormContent.push(
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        }
    )

    const animalMilkRecordingMeasurementSubmit = async (values) => {
        setIsLoading(true);
        const valuesToSend = {}
        valuesToSend.lambingId = fetchedAnimalLambing._id;
        valuesToSend.animalId = selectedAnimal._id;
        valuesToSend.testDate = selectedMilkRecording?.testDate;
        valuesToSend.milkRecording = selectedMilkRecording?._id;
        valuesToSend.measurement = values;

        const checks = [];
        const measurementTypes = ["morning", "noon", "evening"];
        measurementTypes.forEach((type) => {
            if (values[type]) {
                checks.push({
                    type: type,
                    milk: Number(values[type]),
                });
            }
        });

        valuesToSend.checks = checks;
        const { success: scs, message } = await createAnimalMilkRecording(valuesToSend);
        if (scs) {
            success(message);
            setStep(1);
            fetchArrayAnimals();
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleWarningConfirm = () => {
        setWarningDialogOpen(false);
        setStep(2);
    };

    const handleWarningCancel = () => {
        setWarningDialogOpen(false);
        setSelectedAnimal(null);
        setFetchedAnimalLambing(null);
    };

    const animalMilkRecordingMeasurementUpdateSubmit = async (values) => {
        setIsLoading(true);
        const valuesToSend = {};
        const checks = [];
        const measurementTypes = ["morning", "noon", "evening"];
        measurementTypes.forEach((type) => {
            if (values[type]) {
                checks.push({
                    type: type,
                    milk: Number(values[type]),
                });
            }
        });

        valuesToSend.checks = checks;
        valuesToSend.animalStatus = animalStatusTranslationArray.find((trn) => trn.gr === values.status)?.en;
        valuesToSend.sampleNumber = values.sampleNumber;

        const { success: scs, message } = await updateAnimalMilkRecording(selectedAnimalMilkRecording._id, valuesToSend);
        if (scs) {
            success(message);
            fetchData();
            setStep(1);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleDelete = async () => {
        setIsLoading(true);
        const { success: scs, message } = await deleteAnimalMilkRecording(selectedAnimalMilkRecording._id);
        if (scs) {
            setIsPopupOpen(false);
            success(message);
            setStep(1);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            <Dialog
                open={warningDialogOpen}
                title="Προειδοποίηση"
                text={warningMessage}
                confirmButton="Εντάξει"
                cancelButton="Ακύρωση"
                onConfirm={handleWarningConfirm}
                onClose={handleWarningCancel}
            />
            <AreYouSurePopup
                width={"900px"}
                isOpen={isPopupOpen}
                title="Διαγραφή μέτρησης"
                titleColor="black"
                onClose={() => setIsPopupOpen(false)}
                innerText={`Είστε σίγουροι ότι θέλετε να διαγράψετε τη μέτρηση γαλακτοπαραγωγής για την προβατίνα με κωδικό Γενεαλογικού Βιβλίου "${selectedAnimal?.registrationCode}" ;`}
                declineText="Πίσω"
                onDecline={() => setIsPopupOpen(false)}
                acceptText="Ναι, Διαγραφή Μέτρησης"
                onAccept={() => handleDelete()}
            />
            <SelectAnimalPopup
                isOpen={isSelectAnimalPopupOpen}
                onClose={() => setIsAnimalPopupOpen(false)}
                onDecline={() => setIsAnimalPopupOpen(false)}
                animals={animalsForPopup}
                handleAnimalSelectFormSubmit={handleAnimalSelectFormSubmit}
            />
            {step === 1 && (
                <>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Box container flexDirection="column" className={classes.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλογή Ζώου"}
                            </Typography>
                            <Form
                                key={step}
                                content={!isUpdate ? animalFormContent : animalSelectFormContent}
                                onSubmit={!isUpdate ? animalSubmit : handleAnimalDropdownSelectSubmit}
                                toResetForm={false}
                            />
                            <Button
                                onClick={() => setIsMilkRecordingSelected(false)}
                                sx={{
                                    marginTop: "1rem",
                                    padding: "0.5rem 1rem",
                                    backgroundColor: "third",
                                    color: "secondary.main",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    variant: "h5",
                                }}
                            >
                                Πίσω
                            </Button>
                        </Box>
                    </Box>
                    {!isUpdate && (
                        <>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                mt: "3rem",
                            }}>
                                <Typography variant="h5" sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    {"Ζώα από τη συγκεκριμένη μονάδα για τα οποία αναμένεται έλεγχος"}
                                </Typography>
                                <Table
                                    data={animalsWaiting}
                                    noDataText={"Δεν βρέθηκαν εγγραφές!"}
                                    columns={animalsWaitingTableCoumns}
                                    showPageSizeOptions={true}
                                    defaultSorted={[{ id: "flockCode", desc: false }]}
                                />
                            </Box>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                mt: "3rem",
                            }}>
                                <Typography variant="h5" sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    {"Πρόβατα 42 μέρες μετά τον τοκετό για τα οποία δεν υπάρχει καταγεγραμμένος έλεγχος"}
                                </Typography>
                                <Table
                                    data={animalsAfterFortyTwo}
                                    noDataText={"Δεν βρέθηκαν εγγραφές!"}
                                    columns={animalsAfterFortyTwoTableColumns}
                                    showPageSizeOptions={true}
                                    defaultSorted={[{ id: "flockCode", desc: false }]}
                                />
                            </Box>
                        </>
                    )}
                </>
            )}
            {step === 2 && (
                <>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                    }}>
                        <Typography variant="h5" sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {"Επιλεγμένη Προβατίνα"}
                        </Typography>
                        <Table
                            data={[selectedAnimal]}
                            noDataText={"Δεν βρέθηκαν εγγραφές!"}
                            columns={selectedAnimalTableColumns}
                            showPageSizeOptions={false}
                        />
                    </Box>
                    <Box className={classes.outerBox}>
                        <Box container flexDirection="column" className={classes.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Μέτρηση Γαλακτοπαραγωγής"}
                            </Typography>
                            <Form
                                content={animalMilkRecordingFormContent}
                                onSubmit={!isUpdate ? animalMilkRecordingMeasurementSubmit : animalMilkRecordingMeasurementUpdateSubmit}
                                toResetForm={false}
                            />
                            <Button
                                onClick={() => setStep(1)}
                                sx={{
                                    marginTop: "1rem",
                                    padding: "0.5rem 1rem",
                                    backgroundColor: "third",
                                    color: "secondary.main",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    variant: "h5",
                                }}
                            >
                                Πίσω
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </>
    )
}

export default memo(AnimalMilkrecordingForm);
